import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import ProviderBrandedHeader from "@/components/ProviderBrandedHeader";
import wellsuitesLogo from "@/assets/wellsuites-logo-black.png";
import ConsumerHeader from "@/components/ConsumerHeader";
import MobileBookingBar from "@/components/MobileBookingBar";
import FacilitiesSection from "@/components/provider/FacilitiesSection";
import ServicesTab from "@/components/provider/ServicesTab";
import RoomsTab from "@/components/provider/RoomsTab";
import MembershipsTab from "@/components/provider/MembershipsTab";
import LocationSection from "@/components/provider/LocationSection";
import OpeningTimesSection from "@/components/provider/OpeningTimesSection";
import ReviewSection from "@/components/ReviewSection";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useProvider } from "@/hooks/useProviders";
import { useServices } from "@/hooks/useServices";
import { useFacilities } from "@/hooks/useFacilities";
import { useOpeningHours } from "@/hooks/useOpeningHours";
import { useRooms } from "@/hooks/useRooms";
import { useMemberships } from "@/hooks/useMemberships";
import { useDevices } from "@/hooks/useDevices";
import { useProviderOpeningStatus } from "@/hooks/useProviderOpeningStatus";
import { useReviewCount } from "@/hooks/useReviews";
import { useShare } from "@/hooks/useShare";
import InAppBrowserBanner from "@/components/InAppBrowserBanner";
import SEO from "@/components/SEO";

import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw, Star, MapPin, Clock, Share2, ChevronLeft, ChevronRight } from "lucide-react";
import { isInAppBrowser } from "@/utils/browserDetection";

const ProviderProfile = () => {
  const { slug, providerId } = useParams();
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("services");
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const servicesRef = useRef<HTMLDivElement>(null);

  const providerIdentifier = slug || providerId;
  const { data: provider, isLoading: providerLoading, error: providerError, refetch: refetchProvider } = useProvider(providerIdentifier || "");
  const { data: services, isLoading: servicesLoading } = useServices(provider?.id);
  const { data: facilities, isLoading: facilitiesLoading } = useFacilities(provider?.id || "");
  const { data: openingHours, isLoading: hoursLoading } = useOpeningHours(provider?.id || "");
  const { data: rooms, isLoading: roomsLoading } = useRooms(provider?.id);
  const { data: memberships, isLoading: membershipsLoading } = useMemberships(provider?.id);
  const { data: devices } = useDevices(provider?.id);
  const openingStatus = useProviderOpeningStatus(openingHours);
  const { data: reviewCount } = useReviewCount(provider?.id || "");
  const { share } = useShare();

  useEffect(() => {
    if (providerLoading) {
      const timer = setTimeout(() => setLoadingTimeout(true), 5000);
      return () => clearTimeout(timer);
    } else {
      setLoadingTimeout(false);
    }
  }, [providerLoading]);

  const hasActiveDevices = devices && devices.length > 0 && devices.some(device => device.is_active && device.is_enabled);

  // Collect all images from facilities
  const galleryImages = facilities?.filter(f => f.image_url).map(f => f.image_url!) || [];
  if (provider?.image_url && !galleryImages.includes(provider.image_url)) {
    galleryImages.unshift(provider.image_url);
  }

  const handleBookNowClick = () => {
    setActiveTab("services");
    setTimeout(() => {
      servicesRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleShare = () => {
    share({
      title: provider?.name || "",
      text: `Check out ${provider?.name}`,
      url: window.location.href,
    });
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  if (providerLoading) {
    return (
      <div className="min-h-screen bg-background">
        <InAppBrowserBanner />
        <ConsumerHeader />
        <div className="max-w-4xl mx-auto px-4 py-8">
          {loadingTimeout ? (
            <div className="bg-card rounded-2xl border border-border p-8 text-center">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Taking longer than expected</h2>
              <p className="text-muted-foreground mb-6">
                The page is taking a while to load. {isInAppBrowser() && "This might be due to using an in-app browser."}
              </p>
              <Button onClick={() => refetchProvider()} className="rounded-full">
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          ) : (
            <div className="animate-pulse space-y-6">
              <div className="h-80 bg-muted rounded-2xl"></div>
              <div className="h-8 bg-muted rounded w-1/2"></div>
              <div className="h-4 bg-muted rounded w-1/3"></div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (providerError || !provider) {
    return (
      <div className="min-h-screen bg-background">
        <InAppBrowserBanner />
        <ConsumerHeader />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-card rounded-2xl border border-border p-8 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{providerError ? "Failed to Load" : "Not Found"}</h2>
            <p className="text-muted-foreground mb-6">
              {providerError ? "There was an error loading this provider." : "This provider could not be found."}
            </p>
            <Button onClick={() => refetchProvider()} className="rounded-full">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Create CSS custom properties for brand colors
  const brandStyles: React.CSSProperties = {
    fontFamily: provider.brand_font || undefined,
    backgroundColor: provider.brand_background_color || undefined,
    ['--brand-heading-color' as any]: provider.brand_heading_color || undefined,
    ['--brand-body-color' as any]: provider.brand_body_color || undefined,
  };

  const displayName = (provider as any).display_name || provider.name;
  const seoTitle = `${displayName} — Book wellness sessions | Wellsuites`;
  const seoDesc = provider.description
    ? provider.description.slice(0, 155)
    : `Book sauna, cold plunge & recovery sessions at ${displayName} in ${provider.location}. Real-time availability on Wellsuites.`;
  const canonicalUrl = `https://wellsuites.co/provider/${(provider as any).slug || provider.id}`;

  const jsonLd: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: displayName,
    description: provider.description || undefined,
    image: provider.image_url || undefined,
    address: provider.address
      ? { "@type": "PostalAddress", streetAddress: provider.address, addressLocality: provider.location }
      : undefined,
    geo:
      provider.latitude && provider.longitude
        ? { "@type": "GeoCoordinates", latitude: provider.latitude, longitude: provider.longitude }
        : undefined,
    telephone: (provider as any).phone || undefined,
    url: canonicalUrl,
    aggregateRating:
      provider.rating && reviewCount
        ? {
            "@type": "AggregateRating",
            ratingValue: provider.rating,
            reviewCount: reviewCount,
          }
        : undefined,
  };

  return (
    <div 
      className="min-h-screen pb-24 md:pb-0"
      style={brandStyles}
    >
      <SEO
        title={seoTitle}
        description={seoDesc}
        canonical={canonicalUrl}
        image={provider.image_url || undefined}
        type="product"
        jsonLd={jsonLd}
      />
      <style>{`
        ${provider.brand_heading_color ? `
          [data-brand-page] h1, [data-brand-page] h2, [data-brand-page] h3, [data-brand-page] h4 {
            color: ${provider.brand_heading_color} !important;
          }
        ` : ''}
        ${provider.brand_body_color ? `
          [data-brand-page] p, [data-brand-page] .brand-body-text {
            color: ${provider.brand_body_color} !important;
          }
        ` : ''}
      `}</style>
      <InAppBrowserBanner />
      <ProviderBrandedHeader provider={provider} />
      
      <main className="max-w-4xl mx-auto px-4 py-6" data-brand-page>
        {/* Quick Info Bar */}
        <div className="flex items-center justify-between gap-4 mb-6 pb-4 border-b border-border">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
            {/* Rating */}
            <div className="flex items-center gap-1.5">
              <Star className="h-4 w-4 fill-foreground text-foreground" />
              <span className="font-semibold text-foreground">{provider.rating || "New"}</span>
              <span>({reviewCount ?? 0} reviews)</span>
            </div>
            
            {/* Location */}
            <div className="flex items-center gap-1.5">
              <MapPin className="h-4 w-4" />
              <span>{provider.location}</span>
            </div>
            
            {/* Opening status */}
            <div className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              <span className={openingStatus.isOpen ? 'text-green-600 font-medium' : 'text-orange-600 font-medium'}>
                {openingStatus.statusWord || openingStatus.statusText}
              </span>
            </div>
          </div>
          
          {/* Action button */}
          <Button 
            variant="outline"
            size="icon" 
            onClick={handleShare} 
            className="h-9 w-9 rounded-full flex-shrink-0"
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </div>

        {/* Facilities Gallery */}
        <FacilitiesSection 
          facilities={facilities}
          facilitiesLoading={facilitiesLoading}
        />

        {/* Services/Rooms/Memberships Tabs */}
        <div ref={servicesRef} className="mb-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start bg-transparent border-b border-border rounded-none p-0 h-auto gap-6">
              <TabsTrigger 
                value="services" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent bg-transparent px-0 pb-3 pt-0 font-medium"
              >
                Services
              </TabsTrigger>
              <TabsTrigger 
                value="rooms" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent bg-transparent px-0 pb-3 pt-0 font-medium"
              >
                Rooms
              </TabsTrigger>
              <TabsTrigger 
                value="memberships" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent bg-transparent px-0 pb-3 pt-0 font-medium"
              >
                Memberships
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="services" className="mt-6">
              <ServicesTab 
                services={services}
                providerName={provider.name}
                providerId={provider.id}
              />
            </TabsContent>
            
            <TabsContent value="rooms" className="mt-6">
              <RoomsTab 
                rooms={rooms}
                services={services}
                roomsLoading={roomsLoading}
              />
            </TabsContent>
            
            <TabsContent value="memberships" className="mt-6">
              <MembershipsTab 
                memberships={memberships}
                membershipsLoading={membershipsLoading}
                providerId={provider.id}
              />
            </TabsContent>
          </Tabs>
        </div>

        {/* About Section */}
        {provider.description && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-3 text-foreground">About</h2>
            <p className="text-muted-foreground leading-relaxed">{provider.description}</p>
          </div>
        )}

        {/* Reviews Section */}
        <div className="mb-8">
          <ReviewSection providerId={provider.id || ""} />
        </div>

        {/* Location Section */}
        <div className="mb-8">
          <LocationSection 
            address={provider.address} 
            latitude={provider.latitude}
            longitude={provider.longitude}
          />
        </div>

        {/* Opening Times Section */}
        <div className="mb-8">
          <OpeningTimesSection 
            openingHours={openingHours}
            hoursLoading={hoursLoading}
          />
        </div>

        {/* Powered by WellSuites */}
        <div className="flex justify-center items-center gap-2 py-6 mt-4 border-t border-border">
          <span className="text-xs text-muted-foreground">Powered by</span>
          <img 
            src={wellsuitesLogo} 
            alt="WellSuites" 
            className="h-4 opacity-60"
          />
        </div>
      </main>

      {/* Mobile Booking Bar */}
      <MobileBookingBar 
        providerName={provider?.name || ""}
        serviceCount={services?.length || 0}
        onBookNowClick={handleBookNowClick}
        brandColor={(provider as any)?.brand_primary_color}
      />
    </div>
  );
};

export default ProviderProfile;
